/**
 * useMentalityContext — owns the fetch + cancel lifecycle for the
 * mentality overlay.
 *
 * - Re-fetches whenever the panel is open and the module filter changes.
 * - Cancel-via-ref prevents stale payloads from overwriting a newly-set
 *   module, and silences the "set state after unmount" warning when the
 *   overlay closes mid-fetch.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getMentalityContext, type MentalityContext, type ModuleKey,
} from '../../../mentorshipService';

export function useMentalityContext(open: boolean, module: ModuleKey | '') {
    const [context, setContext] = useState<MentalityContext | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const cancelRef = useRef(false);

    const fetchContext = useCallback(async () => {
        setLoading(true);
        setError(null);
        cancelRef.current = false;
        try {
            const data = await getMentalityContext(module || undefined);
            if (cancelRef.current) return;
            setContext(data);
        } catch (err) {
            console.error('[MentalityOverlay] fetch failed', err);
            if (!cancelRef.current) setError('Could not load your mentality overlay.');
        } finally {
            if (!cancelRef.current) setLoading(false);
        }
    }, [module]);

    useEffect(() => {
        if (!open) return;
        void fetchContext();
        return () => { cancelRef.current = true; };
    }, [open, fetchContext]);

    return { context, loading, error, fetchContext, cancelRef };
}
